// 3rd party
import { Component, OnInit, OnDestroy, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs/Rx';
import { MatSidenav } from '@angular/material';

// components
import { AppComponent } from 'app/app.component';
import { LayoutMainComponent } from 'app/shared/layout/layout-main/layout-main.component';

// helpers
import { Helper } from 'app/shared/helpers/helper';

// models
import { InstrumentBand } from 'app/instruments/shared/instrument-band.model';
import { ContactCustomer, ERoleActionWebview } from 'app/shared/models/contact-customer.model';
import { EReferenceModeType } from 'app/shared/models/cal-criterion.model';
import { Instrument } from 'app/instruments/shared/instrument.model';

// services
import { StoredFileService } from 'app/shared/services/stored-file.service';
import { InstrumentService } from 'app/instruments/shared/instrument.service';

@Component({
  selector: 'app-instrument-list',
  templateUrl: './instrument-list.component.html',
  styleUrls: ['./instrument-list.component.css']
})
export class InstrumentListComponent implements OnInit, OnDestroy {

  @ViewChild('sidenavRight') sidenavRight: MatSidenav;

  activeStatus: number = 1;
  cancelSidenavOpen: boolean = false;
  checkedItems: number = 0;
  checkedMenuItems: string[] = [];
  chkAll: boolean;
  columns: any[] = [
    { tag: 'st', name: 'Status', value: 'Translate.InstrumentStatus', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.InstrumentStatus' },
    { tag: 'att', name: 'HasAttachment', value: 'Translate.HasAttachments', isChecked: true, isIconColumn: true, tooltipValue: 'Translate.HasAttachments' },
    { tag: 'c', name: 'Code', value: 'Translate.Code', isChecked: true },
    { tag: 'it', name: 'InstrumentType.Name', value: 'Translate.InstrumentType', isChecked: true },
    { tag: 'su', name: 'Supplier.Name', value: 'Translate.Supplier', isChecked: false },
    { tag: 'b', name: 'ShortBandsDescription', value: 'Translate.Bands', isChecked: false },
    { tag: 'os', name: 'OrderService.Code', value: 'Translate.OrderService', isChecked: true },
    { tag: 'cse', name: 'CustomerSector.Name', value: 'Translate.Sector', isChecked: true },
    { tag: 'se', name: 'CurrentSector.Name', value: 'Translate.LaboratorySector', isChecked: false },
    { tag: 'lc', name: 'LastCalibration', value: 'Translate.LastCalibration', isChecked: true },
    { tag: 'nc', name: 'NextCalibration', value: 'Translate.NextCalibration', isChecked: false },
    { tag: 'dd', name: 'DeliveryDate', value: 'Translate.DeliveryDate', isChecked: true },
    { tag: 'cfm', name: 'CalibrationFrequencyMonths', value: 'Translate.CalibrationFreq', isChecked: false },
    { tag: 'im', name: 'InstrumentModel.Name', value: 'Translate.Model', isChecked: false },
    { tag: 'obs', name: 'Description', value: 'Translate.Observation', isChecked: false },
    { tag: 'sn', name: 'SerialNumber', value: 'Translate.SerialNumber', isChecked: false },
    { tag: 'ia', name: 'IsActive', value: 'Translate.Active', isChecked: false }
  ];
  currentPage: number = 1;
  data: any[];
  filter: string;
  hasAttachmentPermission: boolean = false;
  hasCertificatesListPermission: boolean = false;
  hasInstrumentsViewCriterionAcceptance: boolean = false;
  instrumentLocal: number = 1;
  instrumentsBands: InstrumentBand[] = [];
  isCheckingAll: boolean = false;
  isLoading: boolean = false;
  isSearching: boolean = false;
  orderBy: string = 'code';
  overallCount: number = 1;
  pageSize: number = 25;
  qtVisibleColumns: number = this.columns.filter(c => c.isChecked).length;
  ReferenceModeType: typeof EReferenceModeType = EReferenceModeType;
  search: string = '';
  selectedItems: any[] = [];
  sortOrder: string = 'asc';
  statusList: any[] = [
    { id: -1, name: 'Configurations.FilterAll' },
    { id: 1, name: 'Translate.Active' },
    { id: 0, name: 'Translate.Inactive' }
  ];
  subscribe: Subscription;
  totalPages: number = 1;

  constructor(
    private app: AppComponent,
    private el: ElementRef,
    private instrumentService: InstrumentService,
    public mainLayout: LayoutMainComponent,
    private renderer2: Renderer2,
    private router: Router,
    public storedFileService: StoredFileService
  ) { }

  ngOnInit() {
    let columnTag = localStorage.getItem('instrumentTagName');
    if (columnTag) {
      this.columns.push({ tag: 'tg', name: 'Tag', value: columnTag, isChecked: false });
    }
    this.checkPermissions();
    this.refresh();
    this.checkColumnsPreferences();
  }

  ngOnDestroy() {
    this.subscribe.unsubscribe();
  }

  cboSelectedValueChanged() {
    this.correctSearch(true, false);
    this.refresh(this.search);
  }

  checkAll(ev) {
    this.isCheckingAll = true;
    this.data.forEach((item) => {
      item.checked = ev.source.checked;
    });
    this.isCheckingAll = false;
    this.checkedItems = this.data.filter(item => item.checked).length;
  }

  checkColumn(event, str: string) {
    event.stopPropagation();
    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return;
    }

    if (this.columns.filter(c => c.isChecked).length === 1 && this.columns[idx].isChecked) {
      return;
    }

    this.columns[idx].isChecked = !this.columns[idx].isChecked;
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;

    this.saveColumnsPreferences(this.columns);
  }

  checkColumnsPreferences() {
    if (!localStorage.getItem('webview.instrumentList.columns')) {
      this.saveColumnsPreferences(this.columns);
    }
    const savedColumns = JSON.parse(localStorage.getItem('webview.instrumentList.columns'));

    this.columns.forEach(c => c.isChecked = savedColumns.findIndex(sc => sc === c.tag) !== -1);
    this.qtVisibleColumns = this.columns.filter(c => c.isChecked).length;
  }

  checkPermissions() {
    const contact = JSON.parse(localStorage.getItem('currentContact')) as ContactCustomer;
    this.hasCertificatesListPermission = ContactCustomer.hasPermission(ERoleActionWebview.CertificatesList, contact.RoleWebView.ActionsPermissions);
    this.hasAttachmentPermission = ContactCustomer.hasPermission(ERoleActionWebview.InstrumentsViewAttachments, contact.RoleWebView.ActionsPermissions);
    this.hasInstrumentsViewCriterionAcceptance = ContactCustomer.hasPermission(ERoleActionWebview.InstrumentsViewCriterionAcceptance, contact.RoleWebView.ActionsPermissions);
  }

  closeSidenav() {
    this.sidenavRight.close();
    this.router.navigate([`/${this.app.currentContact.Domain}/instruments/`]);
  }

  enableSearch() {
    this.isSearching = !this.isSearching;
    this.search = '';
  }

  getStatusDescription(status: number) {
    return this.instrumentService.getStatusDescription(status);
  }

  getStatusIcon(status: number) {
    return this.instrumentService.getStatusIcon(status);
  }

  isColumnChecked(str: string): boolean {
    let idx = this.columns.findIndex(c => c.tag === str);
    if (idx === -1) {
      return false;
    }

    return this.columns[idx].isChecked;
  }

  nextPage(e) {
    this.correctSearch(false, false);
    this.currentPage = e;
    this.refresh();
  }

  openDetails(item: any) {
    if (this.cancelSidenavOpen) {
      this.cancelSidenavOpen = false;
      return;
    }

    this.router.navigate([`/${this.app.currentContact.Domain}/instruments/${item.Identifier}`]);
    this.sidenavRight.open();
  }

  onSelectionChange() {
    if (!this.isCheckingAll) {

      if (this.data.filter(item => item.checked).length !== this.data.length && this.chkAll) {
        this.chkAll = false;
      }
      if (this.data.filter(item => item.checked).length === this.data.length && !this.chkAll) {
        this.chkAll = true;
      }

      this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
      this.checkedItems = this.selectedItems.length;
    }
  }

  prevent($event) {
    this.cancelSidenavOpen = true;
    $event.preventDefault();
  }

  previousPage(e) {
    this.correctSearch(false, false);
    this.currentPage = e;
    this.refresh();
  }

  correctSearch(setCurrentPage: boolean, setCleanSearch: boolean) {
    this.currentPage = setCurrentPage ? 1  : this.currentPage;
    this.search = setCleanSearch ? null : this.search;
  }

  refresh(search?: string, orderBy?: string, sortOrder?: string) {
    this.filter = '';
    this.selectedItems = this.data ? this.data.filter(d => d.checked) : [];
    if (search !== undefined) {
      this.search = search;
    }
    if (orderBy) {
      this.orderBy = orderBy;
    }
    if (sortOrder) {
      this.sortOrder = sortOrder;
    }
    if (this.activeStatus !== undefined && this.activeStatus !== -1 && !this.mainLayout.isMobile) {
      this.filter = '"IsActive_=' + this.activeStatus + '"';
    }
    if (this.instrumentLocal !== undefined && this.instrumentLocal !== -1 && !this.mainLayout.isMobile) {
      this.filter += (this.filter ? ', ' : '') + '"CurrentSector.Identifier_is not null"';
    }

    // permissão de visualização por setor
    if (!this.app.currentContact.ViewAllCustomerSectorsWebView) {
      if (this.app.currentContact.CustomerSectorWebView && this.app.currentContact.CustomerSectorWebView.Identifier) {
        this.filter += (this.filter ? ', ' : '') + '"CustomerSector.Identifier_=\'' + this.app.currentContact.CustomerSectorWebView.Identifier + '\'"';
      } else {
        this.filter += (this.filter ? ', ' : '') + '"CustomerSector.Identifier_is null"';
      }
    }
    if (this.app.currentContact !== undefined && this.app.currentContact.Customer !== undefined) {
      this.filter += (this.filter ? ', ' : '') + '"Customer.Identifier_=\'' + this.app.currentContact.Customer.Identifier + '\'"';
    } else {
      this.filter += (this.filter ? ', ' : '') + '"Customer.Identifier_=\'ERROR\'"';
    }
    const lstSpinner = this.el.nativeElement.querySelectorAll('mat-spinner');
    for (let x = 0; x < lstSpinner.length; x++) {
      this.renderer2.setStyle(lstSpinner[x], 'display', 'block');
    }
    this.subscribe = this.instrumentService.get(null, this.search, this.orderBy, this.sortOrder, this.pageSize, this.currentPage, this.filter).subscribe(data => {
      for (let x = 0; x < lstSpinner.length; x++) {
        this.renderer2.setStyle(lstSpinner[x], 'display', 'none');
      }

      this.data = Helper.deserialize(data.text());

      if (this.data.length > 0) {
        this.instrumentService.getbands(this.data as Instrument[]).subscribe();
      }

      if (this.selectedItems) {
        this.data.forEach(d => d.checked = this.selectedItems.find(s => s.Identifier === d.Identifier && s.checked) ? true : false);
      }
      this.checkedItems = this.data.filter(d => d.checked).length;
      this.overallCount = this.data.length > 0 ? this.data[0].OverallCount : 0;
      this.totalPages = Helper.getTotalPages(this.pageSize, this.overallCount);
    });
  }

  saveColumnsPreferences(columns: any[]) {
    localStorage.setItem('webview.instrumentList.columns', JSON.stringify(columns.filter(c => c.isChecked).map(c2 => c2.tag)));
  }

  searchData() {
    this.correctSearch(true, false);
    this.refresh(this.search);
  }

  setPageSize(n) {
    this.pageSize = n;
    this.currentPage = 1;
    this.refresh();
  }

  sortData(data) {
    this.refresh(undefined, data.orderBy, data.sortOrder);
  }
}



// WEBPACK FOOTER //
// ./src/app/instruments/instrument-list/instrument-list.component.ts